<template>
  <div>
    <navbar :links="links" />
    <v-layout
      row
      wrap
      align-center
      class="headline mt-4"
    >
      <span
        v-if="uploadablesNotUsedAsSubContent.length > 1"
      >{{ uploadablesNotUsedAsSubContent.length }}&nbsp;</span>
      <span v-if="isReplaceMode">{{ $t('replaceContent') }}</span>
      <span v-else>{{ $t('uploadContent') }}</span>
      <v-btn
        icon
        class="ml-1 my-0 mr-0"
        :title="$t('supportedFileFormats')"
        @click="showUploadHint = !showUploadHint"
      >
        <v-icon
          v-if="showUploadHint"
          medium
        >
          expand_more
        </v-icon>
        <v-icon
          v-else
          medium
        >
          expand_less
        </v-icon>
      </v-btn>
      <span
        v-if="uploadablesNotUsedAsSubContent.length > 0"
      >&nbsp;({{ formatFileSize(overallFileSize) }})</span>
      <span
        v-if="uploadablesNotUsedAsSubContent.length > 0 && busy"
      >:&nbsp;{{ overallUploadedRounded }}%</span>
      <v-spacer />
      <v-btn
        v-if="done"
        flat
        round
        :small="$vuetify.breakpoint.xs"
        color="msr"
        @click="goToResetContentsPage()"
      >
        <v-icon class="mr-2">
          library_music
        </v-icon>
        {{ $t('contentsLink') }}
      </v-btn>
      <v-btn
        v-if="done"
        flat
        round
        :small="$vuetify.breakpoint.xs"
        color="msr"
        @click="resetAndInitialize()"
      >
        <v-icon class="mr-2">
          add
        </v-icon>
        {{ $t('resetUploadPage') }}
      </v-btn>
      <!-- Progressbar -->
      <v-progress-circular
        v-if="busy"
        v-model="overallUploaded"
        :indeterminate="overallUploaded === 0"
        size="50"
        width="4"
        color="msr"
        class="mx-0 mt-0 mb-2"
      />
    </v-layout>
    <div
      v-if="overallUploaded >= 100.0 && !done"
      class="info-text pa-2 text-xs-center subheading bg-info mb-2"
    >
      {{ $t('contentUploadValidationInProgress') + ' ...' }}
    </div>
    <v-layout column>
      <slot
        v-if="!done"
        name="extra-hint"
      >
        <!-- Intentionally empty -->
      </slot>
      <v-slide-y-transition>
        <v-layout
          v-if="showUploadHint && !done"
          align-start
          justify-space-between
          wrap
          class="mt-3"
        >
          <!-- Info text -->
          <span v-if="isReplaceMode && contentToReplace !== null">
            {{ $t('replaceContentHint_1') }}:
            <router-link
              :to="`/spot/${$route.params.ctype}/${$route.params.spotId}`"
            >{{ contentToReplace.title }}</router-link>
            <span v-if="contentToReplace.ctype === 'tv'">
              ({{ $t('tvformat') }}: {{ contentToReplace.tvformat.name }})
            </span>
            <div class="warning--text py-2">{{ replaceContentHint_2 }}</div>
          </span>
          <span v-else>
            <content-upload-available-formats
              :tvformats="userTVFormatsAvailable"
            />
          </span>
          <!-- Supported file formats -->
          <supported-file-formats
            :audio="!isReplaceMode || $route.params.ctype === 'radio'"
            :video="!isReplaceMode || $route.params.ctype === 'tv'"
            :image="!isReplaceMode || $route.params.ctype === 'tv'"
          />
        </v-layout>
      </v-slide-y-transition>
      <v-layout
        align-start
        justify-space-between
        wrap
      >
        <div v-if="done">
          <!-- Reports -->
          <v-layout
            v-if="done"
            :class="{
              'd-block': uploadSuccessful || $vuetify.breakpoint.mdAndDown,
            }"
            :wrap="$vuetify.breakpoint.mdAndDown"
          >
            <!-- Successful Uploads -->
            <v-alert
              :value="uploadSuccessful || uploadPartiallySuccessful"
              type="success"
              data-cy="success-report"
            >
              <span class="subheading">{{ report }}</span>
            </v-alert>
            <!-- Failed Uploads -->
            <v-alert
              :value="uploadPartiallySuccessful || uploadCompleteFailure"
              type="error"
              data-cy="error-report"
            >
              <span class="subheading">{{ errorReport }}</span>
            </v-alert>
            <!-- Uploads had Warnings -->
            <v-alert
              :value="numWarnings > 0"
              type="warning"
              data-cy="warning-report"
            >
              <span class="subheading">{{ warningReport }}</span>
            </v-alert>
          </v-layout>
        </div>
      </v-layout>
      <!-- Global UI -->
      <v-layout
        align-start
        wrap
        class="mt-3"
      >
        <div
          style="display: flex; align-items: flex-start"
          :class="{
            'mr-5': showUploadGroupSelect || showUploadGroupContentOverlayImage,
          }"
        >
          <!-- Upload Group Dropdown -->
          <div v-if="showUploadGroupSelect">
            <span
              v-if="!selectedUploadGroupId"
              class="v-label mb-2 msr--text"
            >
              <b>{{ $t('chooseUploadGroup') }}:</b>
            </span>
            <upload-group-select
              :selected-upload-group-id.sync="selectedUploadGroupId"
              :loading="busy"
              :hint="!selectedUploadGroupId ? '' : $t('chooseUploadGroup')"
              :confirmation-message="
                selectedUploadGroupId && atLeastOneFileChosen
                  ? $t('chooseUploadGroupConfirm')
                  : null
              "
              style="max-width: 340px"
            />
          </div>
          <!-- Group Content Overlay Image -->
          <v-slide-x-transition>
            <div
              v-if="showUploadGroupContentOverlayImage"
              column
              class="ml-4"
            >
              <div class="caption grey--text text--darken-2 mb-3">
                {{ $t('contentUploadGroupContentImageOverlayHint') }}:
              </div>
              <img
                class="unselectable"
                :src="contentOverlayImage.image_url"
                style="max-width: 100px; max-height: 84px; object-fit: contain"
              >
            </div>
          </v-slide-x-transition>
        </div>
        <!-- Drag and Drop area and browse button-->
        <v-slide-y-reverse-transition>
          <div
            v-if="showDragAndDropArea"
            :class="{'mt-3': $vuetify.breakpoint.lgAndDown}"
            style="flex-grow: 1"
          >
            <drag-and-drop-file-area
              data-cy="drag-and-drop-area"
              :single="isReplaceMode"
              :more="atLeastOneFileChosen"
              :disabled="!!subContentBeingDragged"
              @browse="$refs.fileInput.click()"
              @dropped="onFilesChosen"
            />
            <input
              ref="fileInput"
              :key="'hidden-file-input-' + hiddenFileInputRenderKey"
              type="file"
              hidden
              :multiple="!isReplaceMode"
              :accept="allowedMimeTypes.join(',')"
              data-cy="upload-content-dialog-hidden-file-input"
              @change="onFileInputChanged($event.target)"
            >
          </div>
        </v-slide-y-reverse-transition>
      </v-layout>
      <!-- Modify all uploaded files -->
      <v-layout
        v-if="selectedUploadGroupId && uploadables.length > 0"
        class="mt-4"
        justify-center
        align-center
        :wrap="$vuetify.breakpoint.lgAndDown"
      >
        <!-- All image files: Widget to set duration in seconds -->
        <div v-if="hasAtLeastOneImageFile">
          <v-menu
            v-model="imageDurationMenu"
            offset-y
            lazy
            :disabled="done"
            :close-on-content-click="false"
          >
            <v-btn
              slot="activator"
              round
              flat
              :disabled="done"
            >
              <v-icon
                left
                color="msr"
              >
                av_timer
              </v-icon>
              {{ $t('setImageDurationForAll') }}
            </v-btn>
            <v-list class="pb-0">
              <v-list-tile class="list-tile-auto-height">
                <v-layout align-center>
                  <v-text-field
                    ref="lastdurationwidget"
                    v-model="lastDuration"
                    type="number"
                    :min="minImageDuration"
                    :max="maxImageDuration"
                    :rules="[rules.minImageDuration, rules.maxImageDuration]"
                    step="1"
                    class="mr-2"
                    box
                    :label="$t('seconds')"
                    :title="$t('imageDurationSecondsTooltip')"
                    @keyup.enter="assignImageDurationToAll(lastDuration)"
                    @keyup.esc="imageDurationMenu = false"
                  />
                  <v-btn
                    icon
                    flat
                    color="success"
                    @click="assignImageDurationToAll(lastDuration)"
                  >
                    <v-icon medium>
                      check
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    flat
                    class="mr-0"
                    color="grey darken-2"
                    @click="imageDurationMenu = false"
                  >
                    <v-icon medium>
                      close
                    </v-icon>
                  </v-btn>
                </v-layout>
              </v-list-tile>
            </v-list>
          </v-menu>
        </div>
        <div
          v-if="
            uploadablesNotUsedAsSubContent.length > 0 &&
              $userprofile &&
              $userprofile.can_use_tagging &&
              tagsForChosenUploadGroup.length > 0
          "
          class="mx-2"
        >
          <!-- All files: Dropdown to assign Tags -->
          <tag-select
            :tags="tagsNotUsedOnAllFilesAlready"
            button-icon-class="msr--text"
            button-text-class="grey--text text--darken-3"
            :button-label="$t('setTagsForAll')"
            :show-manage-tags-link="false"
            show-extra-divider
            :disabled="done"
            @add="(tag) => assignTagToAll(tag)"
          >
            <div slot="extra-tile">
              <v-list-tile
                class="grey--text text--darken-2"
                style="text-transform: uppercase"
                @click="clearTagsForAll()"
              >
                {{ $t('clearTagsForAll') }}
              </v-list-tile>
            </div>
          </tag-select>
          <!-- All files: Toggle whether tagged content can be deactivated -->
          <v-layout
            align-center
            nowrap
            class="unselectable ml-3"
            :class="{clickable: !done}"
            style="position: relative; top: -4px"
            @click.stop="
              done
                ? null
                : (tagEntriesCanBeDeactivated = !tagEntriesCanBeDeactivated)
            "
          >
            <span class="ml-1 mr-1">
              <v-icon
                v-if="tagEntriesCanBeDeactivated"
                color="msr"
                small
              >
                lock_open
              </v-icon>
              <v-icon
                v-else
                color="warning"
                small
              > lock </v-icon>
            </span>
            <div
              class="caption pseudo-anchor--inverted"
              :class="{
                'grey--text text--darken-1': tagEntriesCanBeDeactivated,
                'warning--text': !tagEntriesCanBeDeactivated,
              }"
              :title="$t('contentUploadTagEntriesCanBeDeactivatedTooltip')"
            >
              <span v-if="tagEntriesCanBeDeactivated">{{
                $t('contentUploadTagEntriesCanBeDeactivated')
              }}</span>
              <span v-else>{{
                $t('contentUploadTagEntriesCanNotBeDeactivated')
              }}</span>
            </div>
          </v-layout>
        </div>
        <!-- All files: Dropdown to assign a Category -->
        <v-menu
          v-if="uploadables.length > 0"
          offset-y
          :disabled="done"
        >
          <v-btn
            slot="activator"
            round
            flat
            :disabled="done"
          >
            <v-icon
              left
              color="msr"
            >
              folder
            </v-icon>
            {{ $t('setCategoryForAll') }}
          </v-btn>
          <v-list>
            <v-list-tile
              v-for="category in assignableCategoriesForBatchAssignment"
              :key="category.id"
              @click="assignCategoryToAll(category)"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ category.name }}
                </v-list-tile-title>
              </v-list-tile-content>
              <small
                v-if="category.is_default"
                class="grey--text ml-3"
              >{{
                $t('default')
              }}</small>
              <small
                v-else-if="category.group"
                class="grey--text ml-3"
              >{{
                category.group.name
              }}</small>
            </v-list-tile>
          </v-list>
        </v-menu>
        <!-- All files: Dialog to assign a hard-range -->
        <v-btn
          v-if="uploadables.length > 0"
          round
          flat
          :disabled="done"
          @click="showEditHardRangeDialog = !showEditHardRangeDialog"
        >
          <v-icon
            left
            color="msr"
          >
            date_range
          </v-icon>
          {{ $t('contentHardRangeSet') }}
        </v-btn>
        <edit-hard-range-dialog
          v-if="uploadables.length > 0"
          :show.sync="showEditHardRangeDialog"
          :hard-expire="lastChosenHardExpire"
          :hard-start="lastChosenHardStart"
          :loading="busy"
          @confirm="assignHardRangeToAll"
        />
      </v-layout>
    </v-layout>
    <!-- Desktop: Drag and drop videos for bundling hint -->
    <v-slide-y-reverse-transition>
      <v-layout
        v-if="$vuetify.breakpoint.smAndUp && hasBundleableFiles"
        align-center
        class="mt-3 body-1 subheading"
        :class="{
          'grey--text text--darken-3': !subContentBeingDragged,
          'msr--text': !!subContentBeingDragged,
        }"
      >
        <multiformat-bundle-indicator
          is-multiformat-bundle
          class="mr-2"
        />
        {{ $t('contentUploadBundleViaDragAndDropHint') }}
        <img
          src="/static/icons8-drag-left.gif"
          class="ml-2"
          height="36"
          style="opacity: 0.75"
        >
      </v-layout>
    </v-slide-y-reverse-transition>
    <!-- PDF Splitting Placeholders -->
    <v-layout
      v-if="pdfs.length > 0"
      class="mt-5"
      align-center
      wrap
    >
      <v-card
        v-for="(pdfFile, pdfFileIndex) in pdfs"
        :key="pdfFile.name + '-' + pdfFileIndex"
        flat
        class="elevation-3 mr-3 mt-3"
        style="width: fit-content"
      >
        <v-card-title class="pb-2">
          <!-- Remove Button -->
          <v-tooltip left>
            <v-btn
              slot="activator"
              class="ma-0 pa-0 elevation-0"
              style="
                position: absolute;
                top: 0px;
                right: 0px;
                min-width: 24px;
                min-height: 24px;
                max-width: 24px;
                max-height: 24px;
                z-index: 1;
              "
              @click="removePdf(pdfFile)"
            >
              <v-icon> close </v-icon>
            </v-btn>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>
          <!-- Icon and Name -->
          <v-icon
            left
            color="msr"
            medium
          >
            picture_as_pdf
          </v-icon>
          <div>{{ pdfFile.name }}</div>
        </v-card-title>
        <v-card-text class="pt-0 pb-2">
          <span class="mr-2 grey--text text--darken-1">
            {{ $t('uploadPdfIsSplitIntoImages') }}
          </span>
          <v-progress-circular
            indeterminate
            color="msr"
            size="16"
            width="2"
          />
        </v-card-text>
      </v-card>
    </v-layout>
    <!-- Chosen files to upload -->
    <transition-group
      name="uploads-list-transition"
      tag="div"
      class="content-upload-files-container mt-3"
    >
      <content-upload-file
        v-for="(uploadable, uploadableIndex) in uploadablesNotUsedAsSubContent"
        :key="
          selectedUploadGroupId + uploadable.file.name + uploadable.file.type
        "
        :ref="'contentUploadFile_' + uploadableIndex"
        :categories="
          uploadable.isAudio
            ? assignableCategories
            : assignableCategoriesWithoutClosingTime
        "
        :content-overlay-image="contentOverlayImage"
        :default-category="
          selectedUploadGroup ? selectedUploadGroup.default_category : null
        "
        :disable-controls="busy"
        :editable-category="!isReplaceMode"
        :editable-hard-range="!isReplaceMode"
        :editable-title="!isReplaceMode"
        :uploadable="uploadable"
        :sub-content-being-dragged.sync="subContentBeingDragged"
        :show-details.sync="showFileFormErrors"
        :show-video-format-chooser="!isReplaceMode"
        :tags="tagsForChosenUploadGroup"
        :tag-entries-can-be-deactivated="tagEntriesCanBeDeactivated"
        class="content-upload-file pa-1"
        @update:file="(newFile) => onFileChanged(uploadable, newFile)"
        @remove="removeUploadable"
        @maybe-show-format-chooser="
          maybeShowFormatChooserDialogForSingleContent
        "
        @tag-added="onFileTagAdded"
        @tag-removed="onFileTagRemoved"
        @sub-content-dropped="onSubContentDropped($event, uploadable)"
        @sub-content-bundle-disband="onSubContentBundleDisband(uploadable)"
      />
    </transition-group>
    <v-btn
      v-if="splittingPdfs"
      round
      color="error"
      dark
      fixed
      bottom
      right
      class="unclickable"
      :style="{'margin-bottom': showUploadButton ? '96px' : '48px'}"
    >
      <v-icon left>
        picture_as_pdf
      </v-icon>
      {{ pdfs.length }} {{ $t('uploadPdfSplittingInProgress') }} ...
    </v-btn>
    <!-- Note: Moving this btn below the parent layout did actually
		  force a rerendering of the ContentUploadFile list when 'done' was
		  toggled after uploading. This was undesired, as the file widgets
		  would not show any feedback anymore. I am unsure why this happens,
		  may have to do with relayouting? Make sure this does not happen.
      -->
    <v-btn
      v-if="showUploadButton"
      :loading="busy && !done"
      :disabled="!enableUploadButton"
      :dark="enableUploadButton"
      round
      fixed
      bottom
      right
      data-cy="continue-btn"
      :style="{'margin-bottom': showDragAndDropArea ? '48px' : 'initial'}"
      class="msr z-index-1"
      :class="$desktopModeStyles"
      @click.stop="upload()"
    >
      <v-icon left>
        file_upload
      </v-icon>
      <span v-if="uploadables.length === 1 || splittingPdfs">{{
        isReplaceMode ? $t('replaceContent') : $t('uploadContentOne')
      }}</span>
      <span v-else>
        {{ uploadables.length }}
        {{ $t('uploadContent') }}
      </span>
    </v-btn>
    <v-btn
      v-if="showResetButton"
      round
      bottom
      fixed
      right
      class="z-index-1"
      :class="$desktopModeStyles"
      @click="resetAndInitialize()"
    >
      <v-icon left>
        refresh
      </v-icon>
      {{ $t('reset') }}
    </v-btn>
    <div style="min-height: 120px" />
  </div>
</template>

<script>
import axios from 'axios';
import goTo from 'vuetify/es5/components/Vuetify/goTo';
import orderBy from 'lodash.orderby';

import {getMainTVSpotFromBundle} from '@/utils';
import {formatDate} from '@/mixins/timing';
import {
  IMAGE_DURATION_DEFAULT,
  IMAGE_DURATION_MAX,
  IMAGE_DURATION_MIN,
} from '@/mixins/imageDuration';
import uploadGroupSelect from '@/mixins/uploadGroupSelect';

import {Uploadable} from '@/uploadable';

import ContentUploadAvailableFormats from '@/components/ContentUploadAvailableFormats';
import ContentUploadFile from '@/components/ContentUploadFile';
import DragAndDropFileArea from '@/components/DragAndDropFileArea';
import EditHardRangeDialog from '@/components/EditHardRangeDialog';
import MultiformatBundleIndicator from '@/components/MultiformatBundleIndicator';
import Navbar from '@/components/Navbar';
import SupportedFileFormats from '@/components/SupportedFileFormats';
import TagSelect from '@/components/TagSelect';
import UploadGroupSelect from '@/components/UploadGroupSelect';

export const MAX_TOLERANCE_PERCENTAGE = 5.65;

export default {
  components: {
    ContentUploadAvailableFormats,
    ContentUploadFile,
    DragAndDropFileArea,
    EditHardRangeDialog,
    MultiformatBundleIndicator,
    Navbar,
    SupportedFileFormats,
    TagSelect,
    UploadGroupSelect,
  },
  mixins: [uploadGroupSelect],
  data() {
    return {
      /**
       *  Optional Radio/TVSpotBundle that will be replaced with the new content.
       */
      contentToReplace: null,

      allowedFileTypes: [],
      busy: false,
      done: false,
      errorReport: '',
      hiddenFileInputRenderKey: 0,
      imageDurationMenu: false,
      lastChosenHardExpire: null,
      lastChosenHardStart: null,
      lastDuration: IMAGE_DURATION_DEFAULT,
      maxImageDuration: IMAGE_DURATION_MAX,
      minImageDuration: IMAGE_DURATION_MIN,
      numFailed: 0,
      numUploaded: 0,
      numWarnings: 0,
      overallUploaded: 0.0,
      report: '',
      selectedUploadGroupId: null,
      showEditHardRangeDialog: false,
      showFileFormErrors: false,
      showUploadHint: true,
      sizeUploaded: 0,
      subContentBeingDragged: null,
      tagEntriesCanBeDeactivated: true,
      pdfs: [],
      splittingPdfs: false,
      uploadables: [],
      uploadQueue: [],

      // Counters to keep track which Tags are used on all files
      // currently, so we can exclude them from the global Tags
      // dropdown. Since tag assignment is stored within $refs and these
      // are not reactive we need to use this separate mechanism for
      // now.
      tagToUsedCount: {},
    };
  },
  computed: {
    uploadableFileNameToRefName() {
      const fileNameToRefName = {};
      for (let i = 0; i < this.uploadablesNotUsedAsSubContent.length; i++) {
        const uploadable = this.uploadablesNotUsedAsSubContent[i];
        fileNameToRefName[uploadable.file.name] = `contentUploadFile_${i}`;
      }
      return fileNameToRefName;
    },
    showUploadGroupSelect() {
      return this.uploadgroups.length > 1 && !this.isReplaceMode;
    },
    showUploadGroupContentOverlayImage() {
      return this.contentOverlayImage && !this.replacingRadioSpot;
    },
    rules() {
      return {
        minImageDuration: (v) =>
          v >= this.minImageDuration || this.$t('tooSmall'),
        maxImageDuration: (v) =>
          v <= this.maxImageDuration || this.$t('tooBig'),
      };
    },
    contentOverlayImage() {
      if (!this.selectedUploadGroup) {
        return null;
      }
      return this.selectedUploadGroup.content_overlay_image;
    },
    hasAtLeastOneImageFile() {
      return (
        this.uploadablesNotUsedAsSubContent.filter((uploadable) =>
          this.isImageFile(uploadable.file)
        ).length > 0
      );
    },
    hasBundleableFiles() {
      return (
        this.uploadablesNotUsedAsSubContent.filter(
          (uploadable) =>
            this.isVideoFile(uploadable.file) ||
            this.isImageFile(uploadable.file)
        ).length > 1
      );
    },
    fileNameToUsedAsSubContent() {
      const fileNameToUsedAsSubContent = {};
      for (const uploadable of this.uploadables) {
        for (const subContentFile of uploadable.subContentFiles) {
          fileNameToUsedAsSubContent[subContentFile.name] = true;
        }
      }
      return fileNameToUsedAsSubContent;
    },
    uploadablesNotUsedAsSubContent() {
      return orderBy(
        this.uploadables.filter(
          (uploadable) => !this.fileNameToUsedAsSubContent[uploadable.file.name]
        ),
        'file.name'
      );
    },
    tagsForChosenUploadGroup() {
      if (!this.selectedUploadGroupId) {
        return [];
      }
      return this.$store.state.common.tags.filter((tag) => {
        return tag.group === this.selectedUploadGroupId;
      });
    },
    tagsNotUsedOnAllFilesAlready() {
      const tagsNotUsedOnAllFilesAlready = [];
      for (const tag of this.tagsForChosenUploadGroup) {
        const numUploadablesUsingTag = this.tagToUsedCount[tag.id] || 0;
        if (
          numUploadablesUsingTag < this.uploadablesNotUsedAsSubContent.length
        ) {
          tagsNotUsedOnAllFilesAlready.push(tag);
        }
      }
      return tagsNotUsedOnAllFilesAlready;
    },
    defaultCategories() {
      return this.categories.filter((category) => category.is_default);
    },
    nonDefaultCategories() {
      return this.categories.filter((category) => !category.is_default);
    },
    categoriesForSelectedUploadgroup() {
      if (!this.selectedUploadGroupId) {
        return [...this.defaultCategories, ...this.nonDefaultCategories];
      }

      const uploadgroupCategories = this.nonDefaultCategories.filter(
        (category) =>
          !!category.group && category.group.id == this.selectedUploadGroupId
      );
      return [...this.defaultCategories, ...uploadgroupCategories];
    },
    assignableCategories() {
      return this.categoriesForSelectedUploadgroup;
    },
    assignableCategoriesWithoutClosingTime() {
      return this.assignableCategories.filter(
        (category) => !category.is_closingtime
      );
    },
    assignableCategoriesForBatchAssignment() {
      if (this.noAudioFilesChosen) {
        return this.assignableCategoriesWithoutClosingTime;
      }
      return this.assignableCategories;
    },
    isReplaceMode() {
      return this.$route.meta.replace;
    },
    replacingRadioSpot() {
      return (
        this.isReplaceMode &&
        !!this.contentToReplace &&
        this.contentToReplace.ctype == 'radio'
      );
    },
    replaceContentHint_2() {
      if (!this.contentToReplace) {
        return;
      }
      return this.$t('replaceContentHint_2').replace(
        '{{ num }}',
        this.contentToReplace.entries.length
      );
    },
    links() {
      if (this.isReplaceMode && this.contentToReplace) {
        const spotLink = {
          name: this.contentToReplace.title,
          path: `/spot/${this.$route.params.ctype}/${this.$route.params.spotId}`,
        };
        return ['dashboard', 'contents', spotLink, this.$route.name];
      }
      return ['dashboard', 'contents', this.$route.name];
    },
    userTVFormatsChoosable() {
      return this.$store.state.common.userTVFormatsChoosable;
    },
    userTVFormatsAvailable() {
      return this.$store.state.common.userTVFormatsAvailable;
    },
    showUploadButton() {
      return (
        this.uploadables.length > 0 &&
        this.selectedUploadGroupId !== null &&
        !this.done
      );
    },
    enableUploadButton() {
      return !this.splittingPdfs;
    },
    showResetButton() {
      return this.atLeastOneFileChosen && this.showDragAndDropArea;
    },
    showDragAndDropArea() {
      return (
        this.selectedUploadGroupId !== null &&
        !this.busy &&
        !this.done &&
        (!this.isReplaceMode || this.contentToReplace !== null)
      );
    },
    showCategoryForAllDropdown() {
      return this.uploadables.length > 1 && !this.done;
    },
    allowedMimeTypes() {
      const mimeTypes = [];
      for (let i = this.allowedFileTypes.length - 1; i >= 0; i--) {
        const fileType = this.allowedFileTypes[i];
        mimeTypes.push.apply(mimeTypes, fileType.mimeTypes);
      }
      return mimeTypes;
    },
    allowedFileExtensions() {
      const extensions = [];
      for (let i = this.allowedFileTypes.length - 1; i >= 0; i--) {
        const fileType = this.allowedFileTypes[i];
        extensions.push.apply(extensions, fileType.extensions);
      }
      return extensions;
    },
    categories() {
      return this.$store.state.common.categories;
    },
    atLeastOneFileChosen() {
      return this.pdfs.length + this.uploadables.length > 0;
    },
    numAudioFilesChosen() {
      if (!this.uploadables.length) {
        return 0;
      }
      const audioUploadables = this.uploadables.filter((uploadable) =>
        this.isAudioFile(uploadable.file)
      );
      return audioUploadables.length;
    },
    onlyAudioFilesChosen() {
      return (
        this.uploadables.length > 0 &&
        this.numAudioFilesChosen === this.uploadables.length
      );
    },
    noAudioFilesChosen() {
      return this.numAudioFilesChosen === 0;
    },
    overallFileSize() {
      let size = 0;
      this.uploadables.forEach((uploadable) => {
        size += uploadable.file.size;
      });
      return size;
    },
    overallUploadedRounded() {
      return Math.round(this.overallUploaded);
    },
    uploadSuccessful() {
      return this.numUploaded > 0 && this.numFailed === 0;
    },
    uploadPartiallySuccessful() {
      return (
        this.numUploaded > 0 &&
        this.numFailed > 0 &&
        this.numFailed < this.numUploaded
      );
    },
    uploadCompleteFailure() {
      return this.numUploaded > 0 && this.numFailed === this.numUploaded;
    },
    warningReport() {
      if (!this.numWarnings) {
        return '';
      }
      let template = this.$t('contentUploadReportWarning');
      let text = template.replace('{{ numWarnings }}', this.numWarnings);
      return text;
    },
  },
  watch: {
    tagEntriesCanBeDeactivated: function (flag) {
      this.setTagEntriesCanBeDeactivatedForAll(flag);
    },
    /**
     * Auto-focus the duration input on desktop.
     */
    imageDurationMenu: function (menu) {
      if (menu && this.$vuetify.breakpoint.mdAndUp) {
        this.$nextTick(() => {
          // HACK: For some reason we need to wait a bit longer than just the rerender.
          setTimeout(() => {
            this.$refs.lastdurationwidget.focus();
          }, 100);
        });
      }
    },
    selectedUploadGroupId: function (selectedUploadGroupId, oldGroupId) {
      if (!!oldGroupId && !!selectedUploadGroupId) {
        this.reset();
      }
    },
    showDragAndDropArea: function (show) {
      // Scroll down on small devices so DnD area is visible.
      if (show && this.$vuetify.breakpoint.smAndDown) {
        this.$nextTick(() => {
          this.scrollToBottomInstantly();
        });
      }
    },
  },
  mounted() {
    this.$bus.$on('reset-content-upload-page', this.resetAndInitialize);
  },
  methods: {
    /**
     * Send PDF to server to be split into one image per page.
     *
     * The images are then downloaded based on the list of URLs received
     * from the split response and added as individual files just like a
     * user would.
     *
     * Note: To test this locally without CORS errors popping up, make
     *   sure both frontend and server are using the same domain e.g.
     *   via port :9000 behind nginx.
     */
    splitPdfIntoImages(pdfFile) {
      // This will be reset once the last PDF file is removed, either by
      // user action or as it's split-upload finishes.
      this.splittingPdfs = true;

      return this.$api.splitPdfIntoImageFiles(pdfFile).then((splitResponse) => {
        // If PDF has since been removed by the user, ignore this.
        const removed = this.pdfs.indexOf(pdfFile) == -1;
        if (removed) {
          return;
        }

        const imageUrls = (splitResponse.data || {}).images || [];
        const pageNumberPadding = String(imageUrls.length).length;

        const imagePromises = [];
        const imageFiles = [];

        for (let i = 0; i < imageUrls.length; i++) {
          const pageNumber = String(i + 1).padStart(pageNumberPadding, '0');
          const imageUrl = imageUrls[i];
          imagePromises.push(
            axios
              .get(imageUrl, {responseType: 'blob'})
              .then((imageResponse) => {
                const imageBlob = new Blob([imageResponse.data], {
                  type: 'image/png',
                });
                const imageFileName = pdfFile.name.replace(
                  '.pdf',
                  '_' + this.$t('page') + '_' + pageNumber + '.png'
                );
                const imageFile = new File([imageBlob], imageFileName, {
                  type: 'image/png',
                });
                imageFiles.push(imageFile);
              })
          );
        }

        Promise.all(imagePromises).finally(() => {
          // Add split image files to upload queue
          this.onFilesChosen(imageFiles);
          // Cleanup
          this.removePdf(pdfFile);
        });
      });
    },
    /**
     * Try 16:9, 9:16, 32:9 in that order and return matching File.
     *
     * Based on augmented .contentWidth and .contentHeight or (see
     * checkVideo() and checkImage() within ContentUploadFile.vue).
     *
     * If no decision can be made, return null.
     */
    determineMainContentFileByConvention(videoOrImageFiles) {
      const ratios = [
        {name: 'landscape', w: 16, h: 9},
        {name: 'portrait', w: 9, h: 16},
        {name: 'stretch', w: 32, h: 9},
      ];
      for (const {name, w, h} of ratios) {
        for (const file of videoOrImageFiles) {
          const contentWidth = file.contentWidth;
          const contentHeight = file.contentHeight;
          if (!contentWidth || !contentHeight) {
            continue;
          }
          const tolerance = (w / h / 100.0) * MAX_TOLERANCE_PERCENTAGE;
          const aspect = Number(contentWidth / contentHeight);
          const offset = Math.abs(w / h - aspect);
          const isRatio = offset < tolerance;
          if (isRatio) {
            console.log(
              '[INFO] Main video file of ratio ' +
                name +
                ' ' +
                w +
                'x' +
                h +
                ' is: ' +
                file.name
            );
            return file;
          }
        }
      }

      return null;
    },
    onSubContentDropped(draggedFileName, uploadableDroppedOnto) {
      const draggedUploadable = this.uploadables.filter(
        (uploadable) => uploadable.file.name === draggedFileName
      )[0];
      const draggedFile = draggedUploadable.file;

      // Prevent invalid combination.
      if (!draggedUploadable || !draggedFile) {
        return;
      }
      if (draggedUploadable.subContentFiles.length > 0) {
        return;
      }
      if (draggedFile === uploadableDroppedOnto.file) {
        return;
      }

      // Remove Uploadable for dragged file.
      const index = this.uploadables.findIndex(
        (uploadable) => uploadable.file === draggedFile
      );
      this.uploadables.splice(index, 1);

      // The new sub-file may be better suited as the 'main' content by
      // convention (e.g. we want 16:9 to always be the main preview, if
      // available).
      const mainContentFile = this.determineMainContentFileByConvention([
        uploadableDroppedOnto.file,
        draggedFile,
      ]);
      if (mainContentFile !== null && mainContentFile === draggedFile) {
        const previousmainContentFile = uploadableDroppedOnto.file;
        this.$set(uploadableDroppedOnto, 'file', mainContentFile);
        // Since we allow both images and videos and mixing them, we
        // need to update the file type identifier flags.
        uploadableDroppedOnto.isImage = this.isImageFile(mainContentFile);
        uploadableDroppedOnto.isVideo = this.isVideoFile(mainContentFile);

        uploadableDroppedOnto.subContentFiles.push(previousmainContentFile);
      } else {
        // Add dragged file to target's sub-files.
        uploadableDroppedOnto.subContentFiles.push(draggedFile);
      }

      this.$nextTick(() => {
        this.recomputeTagToUsedCount();
        this.updateConnectionBetweenUploadablesAndRefs();
      });
    },
    onSubContentBundleDisband(uploadable) {
      // Create a new Uploadable for each sub-file.
      for (const subContentFile of uploadable.subContentFiles) {
        this.uploadables.push(
          new Uploadable({
            file: subContentFile,
            groupId: this.selectedUploadGroupId,
            isAudio: false,
            isImage: this.isImageFile(subContentFile),
            isVideo: this.isVideoFile(subContentFile),
          })
        );
      }
      // Mark the original Uploadable as having no sub-files.
      this.$set(uploadable, 'subContentFiles', []);

      this.$nextTick(() => {
        this.updateConnectionBetweenUploadablesAndRefs();
      });

      this.$bus.$emit('alert-success', {
        message: 'contentUploadBundleSubContentDisbandedSuccess',
        translate: true,
      });
    },
    onFileTagAdded(tag) {
      if (isNaN(this.tagToUsedCount[tag.id])) {
        this.$set(this.tagToUsedCount, tag.id, 0);
      }
      this.$set(this.tagToUsedCount, tag.id, this.tagToUsedCount[tag.id] + 1);
    },
    onFileTagRemoved(tag) {
      if (isNaN(this.tagToUsedCount[tag.id])) {
        this.$set(this.tagToUsedCount, tag.id, 0);
      }
      this.$set(this.tagToUsedCount, tag.id, this.tagToUsedCount[tag.id] - 1);
    },
    recomputeTagToUsedCount() {
      this.tagToUsedCount = {};
      for (const uploadable of this.uploadablesNotUsedAsSubContent) {
        for (const tag of uploadable.tags) {
          this.onFileTagAdded(tag);
        }
      }
    },
    /**
     * Ensure uploadable.ref points to the right component instance.
     */
    updateConnectionBetweenUploadablesAndRefs() {
      for (const ref of this.getContentUploadFileRefs()) {
        ref.initializeUploadableRef();
      }
    },
    getContentUploadFileRefs() {
      const refs = [];
      for (var i = 0; i < this.uploadablesNotUsedAsSubContent.length; i++) {
        const refName = 'contentUploadFile_' + i;
        // Getting a list of widgets here as the ref is used within a v-for loop.
        const widgets = this.$refs[refName];
        if (widgets) {
          const ref = widgets[0];
          if (ref) {
            refs.push(ref);
          }
        }
      }
      return refs;
    },
    goToResetContentsPage() {
      this.$store.commit('RESET_LAST_CONTENTS_QUERY');
      this.$store.commit('RESET_CONTENT_FILTERS');
      this.$router.push({name: 'contents'});
    },
    scrollToBottomInstantly() {
      window.scroll(0, document.body.scrollHeight);
    },
    setAllowedFileTypes() {
      // Define a list of allowed file types. Each file type can allow
      // for multiple mime-types and extensions (all lower-case). When
      // mapping one to the other, those that come first in the list are
      // preferred.
      const imageFileTypes = [
        {
          type: 'image',
          mimeTypes: ['image/jpeg'],
          extensions: ['.jpg', '.jpeg'],
        },
        {
          type: 'image',
          mimeTypes: ['image/png'],
          extensions: ['.png'],
        },
        {
          type: 'image',
          mimeTypes: ['application/pdf'],
          extensions: ['.pdf'],
        },
      ];
      const videoFileTypes = [
        {
          type: 'video',
          mimeTypes: ['video/mp4', 'video/quicktime'],
          extensions: ['.mp4', '.mov', '.m4v'],
        },
      ];
      const audioFileTypes = [
        {
          type: 'audio',
          mimeTypes: ['audio/mpeg'],
          extensions: ['.mp3'],
        },
        {
          type: 'audio',
          mimeTypes: ['audio/wav'],
          extensions: ['.wav'],
        },
      ];

      this.allowedFileTypes = [];
      if (!this.isReplaceMode || this.$route.params.ctype === 'tv') {
        this.allowedFileTypes = this.allowedFileTypes.concat(imageFileTypes);
      }
      if (!this.isReplaceMode || this.$route.params.ctype === 'tv') {
        this.allowedFileTypes = this.allowedFileTypes.concat(videoFileTypes);
      }
      if (!this.isReplaceMode || this.$route.params.ctype === 'radio') {
        this.allowedFileTypes = this.allowedFileTypes.concat(audioFileTypes);
      }
    },
    goToSpotCategories() {
      if (this.atLeastOneFileChosen) {
        const confirmed = confirm(this.$t('askLeaveUploadPage'));
        if (!confirmed) {
          return;
        }
      }
      this.$router.push('/categories');
    },
    /**
     * Handle file change ourselves, since file.sync does not work
     * properly for file objects used in a v-for loop as it seems.
     *
     * This is triggered e.g. by saving a cropped version of the
     * original file. The file change needs to propagate back down to
     * the ContentUploadFile, because this is where we pull the final
     * file object for upload from.
     *
     * If this is not done, we'd pass the uncropped image to the upload.
     */
    onFileChanged(uploadable, file) {
      this.$set(uploadable, 'file', file);
    },
    collapseAllowedFormatsHint() {
      this.showUploadHint = false;
    },
    setTagEntriesCanBeDeactivatedForAll(flag) {
      for (const uploadable of this.uploadables) {
        uploadable.tagEntriesCanBeDeactivated = flag;
      }
    },
    assignTagToAll(tag) {
      this.getContentUploadFileRefs().forEach((ref) => {
        ref.addTagToSelection(tag);
      });
    },
    clearTagsForAll() {
      this.getContentUploadFileRefs().forEach((ref) => {
        ref.clearTagSelection();
      });
      this.$bus.$emit('alert-success', {
        message: 'tagsRemovedSuccess',
        translate: true,
        timeout: 1000,
      });
    },
    assignCategoryToAll(category) {
      for (const uploadable of this.uploadablesNotUsedAsSubContent) {
        // Not all categories can be assigned to any file type.
        if (!uploadable.isAudio && category.is_closingtime) {
          continue;
        }
        uploadable.categoryId = category.id;
      }
      this.$bus.$emit('alert-success', {
        message: category.name,
        translate: false,
        timeout: 1000,
      });
    },
    assignHardRangeToAll(opts) {
      let newHardExpire = opts.newHardExpire;
      if (newHardExpire) {
        newHardExpire = formatDate(opts.newHardExpire);
      }

      for (const uploadable of this.uploadablesNotUsedAsSubContent) {
        uploadable.hardStart = opts.newHardStart;
        uploadable.hardExpire = newHardExpire;
      }
      this.lastChosenHardStart = opts.newHardStart;
      this.lastChosenHardExpire = newHardExpire;

      this.$bus.$emit('alert-success', {
        message: 'contentHardRangeSetOnAllNewUploads',
        translate: true,
      });
    },
    assignImageDurationToAll(duration) {
      if (
        isNaN(duration) ||
        duration < this.minImageDuration ||
        duration > this.maxImageDuration
      ) {
        return;
      }
      for (const uploadable of this.uploadablesNotUsedAsSubContent) {
        if (!this.isImageFile(uploadable.file)) {
          continue;
        }
        uploadable.duration = duration;
      }
      this.imageDurationMenu = false;
      this.$bus.$emit('alert-success', {
        message: duration + ' ' + this.$t('seconds'),
        translate: false,
        timeout: 1000,
      });
    },
    formatFileSize(fileSize) {
      let kb = fileSize / 1000;
      let mb = kb / 1000;
      let mbClamped = Number(mb.toFixed(1)) > 0;
      let result =
        (mbClamped ? mb : kb).toFixed(1) + ' ' + (mbClamped ? 'MB' : 'KB');
      return result;
    },
    removePdf(pdfFile) {
      let index = this.pdfs.indexOf(pdfFile);
      if (index > -1) {
        this.pdfs.splice(index, 1);
      }

      if (this.pdfs.length == 0) {
        this.splittingPdfs = false;
      }
    },
    removeUploadable(uploadable) {
      let index = this.uploadables.indexOf(uploadable);
      if (index > -1) {
        this.uploadables.splice(index, 1);
      }

      // Full reset if that was the last file.
      if (this.uploadablesNotUsedAsSubContent.length === 0) {
        this.resetAndInitialize();
      }
    },
    fileToExtension_(file) {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return extension;
    },
    mimeTypeToExtension_(mimeType) {
      for (let i = this.allowedFileTypes.length - 1; i >= 0; i--) {
        const fileType = this.allowedFileTypes[i];
        const matchesMimeType = fileType.mimeTypes.indexOf(mimeType) !== -1;
        if (matchesMimeType) {
          const extension = fileType.extensions[0];
          return extension;
        }
      }
      return undefined;
    },
    fileToType_(file) {
      const fileExtension = this.fileToExtension_(file);
      for (let i = this.allowedFileTypes.length - 1; i >= 0; i--) {
        const fileType = this.allowedFileTypes[i];
        const matchesExtension =
          fileType.extensions.indexOf(fileExtension) !== -1;
        if (matchesExtension) {
          return fileType.type;
        }
      }
      return undefined;
    },
    isAllowedFile(file) {
      return this.fileToType_(file) !== undefined;
    },
    isPdfFile(file) {
      return this.fileToExtension_(file) === '.pdf';
    },
    isImageFile(file) {
      return this.fileToType_(file) === 'image';
    },
    isVideoFile(file) {
      return this.fileToType_(file) === 'video';
    },
    isAudioFile(file) {
      return this.fileToType_(file) === 'audio';
    },
    onFileInputChanged(inputElement) {
      this.onFilesChosen(inputElement.files);
    },
    onFilesChosen(fileList) {
      let filesToAdd = [...fileList];

      // This breaks on hot-reload otherwise.
      this.setAllowedFileTypes();
      this.collapseAllowedFormatsHint();

      const numUploadablesBefore = this.uploadables.length;

      if (this.isReplaceMode && filesToAdd.length > 1) {
        this.$bus.$emit('alert-warning', {
          message: 'oneFileOnlyWarning',
          translate: true,
        });
        return;
      }

      // We want a native Array, not a FileList object, so we can
      // splice() etc. Also check for allowed file format and reject as
      // needed.
      for (const file of filesToAdd) {
        if (!this.isAllowedFile(file)) {
          const message = this.$t('invalidFileFormat') + ': ' + file.name;
          this.$bus.$emit('alert-warning', {
            message: message,
            translate: false,
            timeout: 8000,
          });
          continue;
        }

        // PDF? Split into pages, then add each as image separately.
        if (this.isPdfFile(file)) {
          this.splitPdfIntoImages(file);
          this.pdfs.push(file);
          continue;
        }

        const uploadable = new Uploadable({
          file: file,
          groupId: this.selectedUploadGroupId,
          isAudio: this.isAudioFile(file),
          isImage: this.isImageFile(file),
          isVideo: this.isVideoFile(file),
        });
        this.uploadables.push(uploadable);
      }

      const hadFilesBefore = this.uploadables.length > numUploadablesBefore;
      if (hadFilesBefore) {
        // Scroll new file into focus to let user know it has been added.
        this.$nextTick(() => {
          const lastUploadable = this.uploadables[this.uploadables.length - 1];
          const refName =
            this.uploadableFileNameToRefName[lastUploadable.file.name];
          const ref = this.$refs[refName][0]; // It's a list due to v-for.
          setTimeout(() => {
            ref.$el.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }, 100);
        });
      }
    },
    /**
     * Handle image/audio/video upload and success/failure.
     *
     * If images/videos are bundled (subContentFiles), we need to upload
     * the main video first, create new Uploadables for its sub-files
     * and pass the main TVSpot's ID to them for bundling.
     *
     * On completion the @completed event is emitted.
     */
    upload() {
      if (!this.uploadables) {
        return;
      }

      // Scroll to top of page.
      goTo(0);

      // Check that all file widgets pass internal validation (e.g.
      // they have a valid title and spot category).

      for (let i = 0; i < this.getContentUploadFileRefs().length; i++) {
        let ref = this.getContentUploadFileRefs()[i];
        if (ref) {
          if (ref.isFormValid()) {
            this.showFileFormErrors = false;
          } else {
            this.showFileFormErrors = true;
            this.$bus.$emit('alert-warning', {
              message: 'contentUploadInvalidForm',
              timeout: 5000,
              translate: true,
            });
            return;
          }
        }
      }

      this.clearUploadQueue();
      for (const uploadable of this.uploadablesNotUsedAsSubContent) {
        this.uploadQueue.push(uploadable);
      }

      const vm = this;

      async function processUploadQueue() {
        vm.busy = true;
        vm.sizeUploaded = 0;
        vm.numUploaded = 0;
        vm.numFailed = 0;

        const percentagePerFile = 100.0 / vm.uploadQueue.length;

        while (vm.uploadQueue.length > 0) {
          const uploadable = vm.uploadQueue.shift();

          const config = {
            onUploadProgress(event) {
              // Don't update when the request has been cancelled.
              if (!vm.uploadables.length) {
                return;
              }
              let current = event.loaded * 100.0;
              let currentFilePerc = Math.round(current / event.total);
              if (uploadable.ref) {
                vm.$nextTick(() => {
                  uploadable.ref.uploading = true;
                  uploadable.ref.uploadProgress = currentFilePerc;

                  let overallCurrentFilePerc =
                    percentagePerFile * (currentFilePerc / 100.0);
                  vm.overallUploaded =
                    vm.numUploaded * percentagePerFile + overallCurrentFilePerc;

                  if (vm.overallUploaded > 100.0) {
                    // Safari seems to get an extra update here,
                    // showing 200% as a result when done. Cap that.
                    vm.overallUploaded = 100.0;
                  }

                  if (currentFilePerc >= 100.0) {
                    // We may get here multiple times for the same file,
                    // make sure to only count it once (e.g. iOS sends us
                    // here twice for every file).
                    if (uploadable.ref.fullyUploaded) {
                      uploadable.ref.uploading = false;
                      return;
                    }

                    uploadable.ref.uploading = false;
                    uploadable.ref.fullyUploaded = true;

                    vm.sizeUploaded += uploadable.file.size;
                    vm.numUploaded += 1;
                  }
                });
              }
            },
          };

          let promise;
          const contentToReplaceId = vm.contentToReplace
            ? vm.contentToReplace.id
            : null;

          if (uploadable.isImage) {
            promise = vm.$api.uploadImage(
              uploadable.title,
              uploadable.groupId,
              uploadable.categoryId,
              uploadable.hardStart,
              uploadable.hardExpire,
              uploadable.file,
              contentToReplaceId,
              uploadable.duration,
              uploadable.tags,
              uploadable.tagEntriesCanBeDeactivated,
              uploadable.bundleMainTvSpotId,
              config
            );
          } else if (uploadable.isVideo || uploadable.isAudio) {
            promise = vm.$api.uploadVideoOrAudio(
              uploadable.title,
              uploadable.groupId,
              uploadable.categoryId,
              uploadable.hardStart,
              uploadable.hardExpire,
              uploadable.file,
              contentToReplaceId,
              uploadable.chosenTVFormats,
              uploadable.tags,
              uploadable.tagEntriesCanBeDeactivated,
              uploadable.bundleMainTvSpotId,
              config
            );
          }

          promise.then((response) => {
            // Also upload sub-files and tell the backend to put them
            // into the same bundle.
            if (
              uploadable.subContentFiles &&
              uploadable.subContentFiles.length > 0 &&
              response &&
              response.data
            ) {
              // This response data shape is specific to our internal
              // upload view and partially mirrored by the client upload
              // endpoints.
              const bundleMainTvSpotId = response.data[0].idnr;

              for (const subContentFile of uploadable.subContentFiles) {
                const subContentUploadable = new Uploadable({
                  bundleMainTvSpotId: bundleMainTvSpotId,
                  categoryId: uploadable.categoryId,
                  chosenTVFormats: [],
                  duration: uploadable.duration,
                  file: subContentFile,
                  groupId: uploadable.groupId,
                  hardExpire: uploadable.hardExpire,
                  hardStart: uploadable.hardStart,
                  isAudio: false,
                  isImage: vm.isImageFile(subContentFile),
                  isVideo: vm.isVideoFile(subContentFile),
                  ref: uploadable.ref,
                  subContentFiles: [],
                  tags: uploadable.tags,
                  tagEntriesCanBeDeactivated:
                    uploadable.tagEntriesCanBeDeactivated,
                  title: uploadable.title,
                });
                vm.uploadQueue.unshift(subContentUploadable);
              }
            }

            if (!response.data.length) {
              return;
            }
            let warnings = response.data[0].warnings || [];
            if (!warnings.length) {
              return;
            }
            if (uploadable.ref) {
              vm.numWarnings += 1;
              let translated = warnings.map((warning) => vm.$t(warning));
              uploadable.ref.warningMessage = translated.join(', ');
            }
          });

          // Upload one by one, not simultaneously.
          try {
            await promise;
          } catch (error) {
            // Ensure the content list will include the partially uploaded content(s).
            vm.$store.commit('INVALIDATE_CONTENTS_CACHE');

            // Cancellation is fine, ignore it.
            if (axios.isCancel(error)) {
              return new Promise((resolve, reject) => {
                vm.$bus.$emit('alert-info', {
                  message: 'uploadCancelled',
                  translate: true,
                });
                resolve();
              });
            }

            let message = 'Upload failed';
            console.error(message);

            // Show the first part of whatever we get as an error text.
            // By convention, the first word may be a translatable
            // error description, whereas the rest may include details
            // like where the problem occurred.
            let rawError;
            try {
              rawError = String(error.response.data);
            } catch (_) {
              rawError = error.toString();
            }
            if (rawError) {
              console.error(rawError);
              message = rawError.substring(0, 240);
              const words = message.split(' ');
              if (words.length > 0) {
                message = vm.$t(words[0]) + ' ' + words.slice(1).join(' ');
              }
              console.error(message);
            }

            vm.numFailed += 1;

            if (uploadable.ref) {
              // FIXME: This uploadable may not show the failureMessage
              //   when bundled. Need to update the correct uploadable.
              uploadable.ref.uploading = false;
              uploadable.ref.uploadFailed = true;
              uploadable.ref.failureMessage = message;
            }
          }
        }

        vm.busy = false;
        // Once we've gone through the queue, prevent reusing the page.
        // We may still view success/error feedback, but another upload
        // will need us to reload the page to ensure everything is reset.
        vm.done = true;

        // Ensure the content list will include the newly uploaded content(s).
        vm.$store.commit('INVALIDATE_CONTENTS_CACHE');

        const numSucceeded = vm.numUploaded - vm.numFailed;
        if (numSucceeded > 0) {
          vm.report = vm.makeSuccessReport(
            numSucceeded,
            vm.formatFileSize(vm.sizeUploaded)
          );
        }
        if (vm.numFailed > 0) {
          vm.errorReport = vm.makeErrorReport(vm.numFailed);
        }

        // Ensure contents list will show new files.
        vm.$store.commit('RESET_CONTENTS');

        vm.$emit('completed');
      }

      processUploadQueue();

      // We need to reset the element explicitly to make sure we
      // trigger a 'changed' event when uploading the same file again.
      this.resetFileInput();
    },
    abort() {
      let confirmed = true;
      if (this.busy) {
        confirmed = confirm(this.$t('askAbortUpload'));
      }
      if (!confirmed) {
        return;
      }

      this.reset();
      this.$api.cancelCurrentRequest();
      this.busy = false;
      this.closeDialog();
    },
    clearUploadQueue() {
      this.uploadQueue = [];
    },
    resetFileInput() {
      this.hiddenFileInputRenderKey += 1;
    },
    resetAndInitialize() {
      this.reset();
      this.initialize();
    },
    reset(keepChosenUploadGroup = false) {
      this.resetFileInput();

      if (!keepChosenUploadGroup) {
        this.keepChosenUploadGroup = this.getInitialUploadgroupId();
      }

      this.splittingPdfs = false;
      this.pdfs = [];
      this.busy = false;
      this.uploadables = [];
      this.done = false;
      this.errorReport = '';
      this.numFailed = 0;
      this.numUploaded = 0;
      this.numWarnings = 0;
      this.overallUploaded = 0;
      this.report = '';
      this.showFileFormErrors = false;
      this.sizeUploaded = 0;
      this.tagEntriesCanBeDeactivated = true;
      this.title = '';
      this.uploadQueue = [];
    },
    makeSuccessReport(numUploaded, sizeUploaded) {
      let text = this.$t('contentUploadReportSuccess');
      text = text.replace('{{ numUploaded }}', numUploaded);
      text = text.replace('{{ size }}', sizeUploaded);
      return text;
    },
    makeErrorReport(numFailed) {
      let text = this.$t('contentUploadReportFailed');
      text = text.replace('{{ numFailed }}', numFailed);
      return text;
    },
    fetchContentToReplace() {
      const ctype = this.$route.params.ctype;
      this.$api
        .retrieveContent(ctype, this.$route.params.spotId)
        .then((response) => {
          if (ctype === 'tv') {
            this.contentToReplace = getMainTVSpotFromBundle(response.data);
          } else {
            this.contentToReplace = response.data;
          }

          // We have to call this again here as the Group will now be
          // based on the content we want to replace.
          this.selectedUploadGroupId = this.getInitialUploadgroupId();
        });
    },
    /**
     * Fetch only if we haven't done that yet.
     */
    fetchUserTVFormatsChoosableIfNeeded() {
      if (this.userTVFormatsChoosable.length) {
        return Promise.resolve();
      }
      return this.$store.dispatch('LIST_USER_TVFORMATS_CHOOSABLE');
    },
    /**
     * Fetch only if we haven't done that yet.
     */
    fetchUserTVFormatsAvailableIfNeeded() {
      if (this.userTVFormatsAvailable.length) {
        return Promise.resolve();
      }
      return this.$store.dispatch('LIST_USER_TVFORMATS_AVAILABLE');
    },
    /**
     * Fetch only if we haven't done that yet.
     */
    fetchSpecialTVFormatsIfNeeded() {
      if (this.$store.state.common.specialTVFormatsAllowedToUpload.length) {
        return Promise.resolve();
      }
      return this.$store.dispatch('LIST_SPECIAL_TVFORMATS_ALLOWED_TO_UPLOAD');
    },
    initialize() {
      this.contentToReplace = null;
      this.showUploadHint = true;
      this.setAllowedFileTypes();

      this.busy = true;

      const categoriesPromise = this.$store.dispatch('LIST_CATEGORIES');
      const userPromise = this.$store.dispatch('RETRIEVE_CURRENT_USER');
      userPromise.then(() => {
        this.selectedUploadGroupId = this.getInitialUploadgroupId();
      });
      const contentToReplacePromise = this.isReplaceMode
        ? this.fetchContentToReplace()
        : Promise.resolve();
      const tvformatsPromise1 = this.fetchSpecialTVFormatsIfNeeded();
      const tvformatsPromise2 = this.fetchUserTVFormatsChoosableIfNeeded();
      const tvformatsPromise3 = this.fetchUserTVFormatsAvailableIfNeeded();

      return Promise.all([
        categoriesPromise,
        userPromise,
        contentToReplacePromise,
        tvformatsPromise1,
        tvformatsPromise2,
        tvformatsPromise3,
      ]).finally(() => {
        this.busy = false;
      });
    },
    /**
     * Open format chooser if added content needs reformatting and is the only one.
     *
     * The dialog only allows to pick formats for a single video, so for now we
     * only want to trigger this when a single content is in the upload queue.
     */
    maybeShowFormatChooserDialogForSingleContent() {
      if (this.uploadablesNotUsedAsSubContent.length !== 1) {
        return;
      }

      const uploadable = this.uploadablesNotUsedAsSubContent[0];
      if (uploadable.subContentFiles.length > 0) {
        return;
      }

      if (this.userTVFormatsChoosable.length <= 1) {
        return;
      }

      const ref = this.getContentUploadFileRefs()[0];
      ref.showVideoFormatChooserDialog = true;
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.reset();
      vm.initialize();
    });
  },
  beforeRouteLeave(to, from, next) {
    this.reset();
    next();
  },
};
</script>

<style scoped>
.edge-btn-height-fix {
  min-height: 36px;
}

.content-upload-files-container {
  display: flex;
  flex-wrap: wrap;
  margin-top: -16px;
  margin-right: -16px;
}
.content-upload-files-container > .content-upload-file {
  margin-top: 16px;
  margin-right: 16px;
}

.uploads-list-transition-enter-active,
.uploads-list-transition-leave-active {
  transition: all 0.3s ease-in-out;
}
.uploads-list-transition-enter,
.uploads-list-transition-leave-to {
  opacity: 0;
  transform: translateX(50px);
}
</style>



// WEBPACK FOOTER //
// src/pages/ContentUpload.vue